import { computed, ref } from 'vue-demi';
import type { Ref, ComputedRef } from 'vue-demi';
import {
  isValidName,
  isValidEmail,
  isValidAge,
  isValidPhone,
  isValidCity,
  isValidZip,
  isBlockedState,
  isWithinSubmitMaxLength,
} from '../shared/validators';
import t from '../render/utils/i18n';

export type FormFieldName =
  | 'firstName'
  | 'lastName'
  | 'age'
  | 'countryCode'
  | 'phoneNumber'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'email'
  | 'smsCode';

type FieldErrors = Partial<Record<FormFieldName, string>>;
type FieldTouched = Partial<Record<FormFieldName, boolean>>;

export type FormSnapshot = {
  firstName: string;
  lastName: string;
  age: string;
  countryCode: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  smsCode: string;
};

export type UseFormFieldsReturn = {
  values: { [K in FormFieldName]: Ref<string> };
  errors: Ref<FieldErrors>;
  canSubmit: ComputedRef<boolean>;
  setValue(field: FormFieldName, value: string): void;
  validate(field?: FormFieldName): boolean;
  reset(): void;
  snapshot(): FormSnapshot;
};

export function useFormFields(opts?: {
  defaultCountryCode?: string;
}): UseFormFieldsReturn {
  const values = {
    firstName: ref(''),
    lastName: ref(''),
    age: ref(''),
    countryCode: ref(opts?.defaultCountryCode ?? '+852'),
    phoneNumber: ref(''),
    addressLine1: ref(''),
    addressLine2: ref(''),
    city: ref(''),
    state: ref(''),
    zipCode: ref(''),
    email: ref(''),
    smsCode: ref(''),
  };
  const errors = ref<FieldErrors>({});
  const touched = ref<FieldTouched>({});

  function getFieldError(field: FormFieldName): string | undefined {
    const v = values[field].value.trim();
    if (field === 'addressLine2') {
      return isWithinSubmitMaxLength(field, v)
        ? undefined
        : t('amoe.form.addressLine2.error.maxLength');
    }
    if (v.length === 0) {
      return t(`amoe.form.${field}.error.required`);
    }
    if (field !== 'age' && !isWithinSubmitMaxLength(field, v)) {
      return t(`amoe.form.${field}.error.maxLength`);
    }
    switch (field) {
      case 'firstName':
      case 'lastName':
        return isValidName(v)
          ? undefined
          : t(`amoe.form.${field}.error.invalid`);
      case 'age':
        return isValidAge(v) ? undefined : t('amoe.form.age.error.invalid');
      case 'phoneNumber':
        return isValidPhone(v)
          ? undefined
          : t('amoe.form.phoneNumber.error.invalid');
      case 'city':
        return isValidCity(v) ? undefined : t('amoe.form.city.error.invalid');
      case 'state':
        return isBlockedState(v)
          ? t('amoe.form.state.error.blocked')
          : undefined;
      case 'zipCode':
        return isValidZip(v) ? undefined : t('amoe.form.zipCode.error.invalid');
      case 'email':
        return isValidEmail(v) ? undefined : t('amoe.form.email.error.invalid');
      default:
        return undefined;
    }
  }

  function validate(field?: FormFieldName): boolean {
    if (field === undefined) {
      const allFields = Object.keys(values) as FormFieldName[];
      const nextTouched: FieldTouched = {};
      const nextErrors: FieldErrors = {};
      for (const f of allFields) {
        nextTouched[f] = true;
        const err = getFieldError(f);
        if (err) nextErrors[f] = err;
      }
      touched.value = nextTouched;
      errors.value = nextErrors;
      return Object.keys(nextErrors).length === 0;
    }
    touched.value = { ...touched.value, [field]: true };
    const err = getFieldError(field);
    const next: FieldErrors = { ...errors.value };
    if (err) next[field] = err;
    else delete next[field];
    errors.value = next;
    return err === undefined;
  }

  function setValue(field: FormFieldName, value: string): void {
    values[field].value = value;
    if (touched.value[field] || errors.value[field]) {
      validate(field);
    }
  }

  const canSubmit = computed(() => {
    const allFields = Object.keys(values) as FormFieldName[];
    return allFields.every((f) => getFieldError(f) === undefined);
  });

  function reset(): void {
    values.firstName.value = '';
    values.lastName.value = '';
    values.age.value = '';
    values.countryCode.value = opts?.defaultCountryCode ?? '+852';
    values.phoneNumber.value = '';
    values.addressLine1.value = '';
    values.addressLine2.value = '';
    values.city.value = '';
    values.state.value = '';
    values.zipCode.value = '';
    values.email.value = '';
    values.smsCode.value = '';
    errors.value = {};
    touched.value = {};
  }

  function snapshot(): FormSnapshot {
    return {
      firstName: values.firstName.value.trim(),
      lastName: values.lastName.value.trim(),
      age: values.age.value.trim(),
      countryCode: values.countryCode.value,
      phoneNumber: values.phoneNumber.value.trim(),
      addressLine1: values.addressLine1.value.trim(),
      addressLine2: values.addressLine2.value.trim(),
      city: values.city.value.trim(),
      state: values.state.value,
      zipCode: values.zipCode.value.trim(),
      email: values.email.value.trim(),
      smsCode: values.smsCode.value.trim(),
    };
  }

  return { values, errors, canSubmit, setValue, validate, reset, snapshot };
}
